import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Milk,
  Tractor,
  Calculator,
  FlaskConical,
  CalendarDays,
  ClipboardList,
  ShieldCheck,
  ScanSearch,
  Users as UsersIcon,
  Package,
  Building2,
  Tags,
  ShoppingCart,
  Truck,
  Boxes,
  Layers,
  History,
  Settings as SettingsIcon,
  UserCog,
  Clock,
  ListChecks,
  MessageSquareWarning,
  Menu,
  ChevronDown,
  LogOut,
  KeyRound,
  CreditCard,
  BookOpen,
  Wallet,
  Gauge,
  BarChart3,
  ArrowLeftRight,
  Trash2,
  ShieldAlert,
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { InstallHint } from "@/components/InstallHint";
import { QuickActions } from "@/components/QuickActions";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { appPath } from "@/lib/paths";
import { getCompanySettings, changePassword } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { NotificationCenter } from "@/components/NotificationCenter";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/i18n/I18nContext";

// Standalone top-level items (shown above the grouped modules)
const TOP_NAV = [
  { to: "/", labelKey: "nav.dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
];

// Grouped, collapsible modules
const MODULES = [
  {
    id: "compliance",
    labelKey: "nav.modCompliance",
    icon: ShieldCheck,
    items: [
      { to: "/haccp", labelKey: "nav.haccp", icon: ShieldCheck, testid: "nav-haccp" },
      { to: "/traceability", labelKey: "nav.traceability", icon: ScanSearch, testid: "nav-traceability" },
      { to: "/nonconformities", labelKey: "nav.nonconformities", icon: ShieldAlert, testid: "nav-nonconformities", roleOnly: ["admin", "manager", "qa"] },
    ],
  },
  {
    id: "reception",
    labelKey: "nav.modMilkReception",
    icon: Milk,
    items: [
      { to: "/milk", labelKey: "nav.milk", icon: Milk, testid: "nav-milk" },
      { to: "/suppliers", labelKey: "nav.milkSuppliers", icon: Tractor, testid: "nav-suppliers" },
      { to: "/raw-materials", labelKey: "nav.rawMaterials", icon: FlaskConical, testid: "nav-raw-materials", sectionKey: "milk" },
      { to: "/material-suppliers", labelKey: "nav.materialSuppliers", icon: Package, testid: "nav-material-suppliers", sectionKey: "suppliers" },
    ],
  },
  {
    id: "production",
    labelKey: "nav.modProduction",
    icon: FlaskConical,
    items: [
      { to: "/productions", labelKey: "nav.productions", icon: FlaskConical, testid: "nav-productions" },
      { to: "/subproducts", labelKey: "nav.subproducts", icon: Boxes, testid: "nav-subproducts", sectionKey: "stock" },
      { to: "/products", labelKey: "nav.products", icon: Layers, testid: "nav-products", roles: ["admin", "manager"] },
      { to: "/planning", labelKey: "nav.planning", icon: CalendarDays, testid: "nav-planning" },
      { to: "/calculator", labelKey: "nav.calculator", icon: Calculator, testid: "nav-calculator" },
      { to: "/yield-simulator", labelKey: "nav.yieldSimulator", icon: Gauge, testid: "nav-yield-simulator" },
      { to: "/templates", labelKey: "nav.templates", icon: ClipboardList, testid: "nav-templates" },
    ],
  },
  {
    id: "stock",
    labelKey: "nav.modStockOrders",
    icon: Package,
    items: [
      { to: "/stock", labelKey: "nav.stock", icon: Package, testid: "nav-stock" },
      { to: "/stock-movements", labelKey: "nav.stockMovements", icon: ArrowLeftRight, testid: "nav-stock-movements", sectionKey: "stock" },
      { to: "/losses", labelKey: "nav.losses", icon: Trash2, testid: "nav-losses", sectionKey: "stock" },
      { to: "/orders", labelKey: "nav.orders", icon: ShoppingCart, testid: "nav-orders" },
      { to: "/delivery-notes", labelKey: "nav.deliveryNotes", icon: Truck, testid: "nav-delivery-notes" },
      { to: "/clients", labelKey: "nav.clients", icon: Building2, testid: "nav-clients" },
      { to: "/price-lists", labelKey: "nav.priceLists", icon: Tags, testid: "nav-price-lists" },
      { to: "/product-packaging", labelKey: "nav.productPackaging", icon: Boxes, testid: "nav-product-packaging", roles: ["admin", "manager"] },
    ],
  },
  {
    id: "workforce",
    labelKey: "nav.modWorkforce",
    icon: UserCog,
    items: [
      { to: "/operators", labelKey: "nav.operators", icon: UserCog, testid: "nav-operators" },
      { to: "/time-clock", labelKey: "nav.timeClock", icon: Clock, testid: "nav-time-clock" },
      { to: "/tasks", labelKey: "nav.tasks", icon: ListChecks, testid: "nav-tasks" },
      { to: "/complaints", labelKey: "nav.complaints", icon: MessageSquareWarning, testid: "nav-complaints" },
    ],
  },
  {
    id: "finance",
    labelKey: "nav.modFinance",
    icon: Wallet,
    items: [
      { to: "/expenses", labelKey: "nav.expenses", icon: Wallet, testid: "nav-expenses", roleOnly: ["admin", "manager"] },
      { to: "/reports", labelKey: "nav.reports", icon: BarChart3, testid: "nav-reports", roleOnly: ["admin", "manager"] },
    ],
  },
  {
    id: "admin",
    labelKey: "nav.modSettingsAdmin",
    icon: SettingsIcon,
    items: [
      { to: "/settings", labelKey: "nav.settings", icon: SettingsIcon, testid: "nav-settings", roles: ["admin", "manager"] },
      { to: "/billing", labelKey: "nav.billing", icon: CreditCard, testid: "nav-billing", roles: ["admin"] },
      { to: "/permissions", labelKey: "nav.permissions", icon: KeyRound, testid: "nav-permissions", roles: ["admin"] },
      { to: "/users", labelKey: "nav.users", icon: UsersIcon, testid: "nav-users", roles: ["admin"] },
      { to: "/activity", labelKey: "nav.activity", icon: History, testid: "nav-activity" },
      { to: "/guide", labelKey: "nav.guide", icon: BookOpen, testid: "nav-guide" },
    ],
  },
];

const ALL_NAV = [...TOP_NAV, ...MODULES.flatMap((m) => m.items)];

// Section key that owns a nav item ("/" -> "dashboard", "/delivery-notes" -> "delivery-notes").
const keyOf = (to) => (to === "/" ? "dashboard" : to.replace(/^\//, ""));

const NAV_STORAGE_KEY = "dairyops_nav_open";

// Which module (if any) owns the current path — used to auto-expand the active section.
const moduleForPath = (pathname) => {
  const m = MODULES.find((mod) =>
    mod.items.some((it) => {
      const to = appPath(it.to);
      return pathname === to || pathname.startsWith(`${to}/`);
    })
  );
  return m?.id || null;
};

const hexToHsl = (hex) => {
  if (!hex || typeof hex !== "string") return null;
  let m = hex.replace("#", "").trim();
  if (m.length === 3) m = m.split("").map((c) => c + c).join("");
  if (m.length !== 6 || /[^0-9a-fA-F]/.test(m)) return null;
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

const Brand = ({ brandName, logoUrl }) => {
  const { t } = useT();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const src = logoUrl ? (logoUrl.startsWith("http") ? logoUrl : `${backendUrl}${logoUrl}`) : null;
  return (
    <div className="flex items-center gap-2.5 px-2">
      {src ? (
        <img src={src} alt={brandName || "Logo"} className="h-9 w-9 rounded-lg object-contain shadow-sm" data-testid="brand-logo" />
      ) : (
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[hsl(var(--brand))] text-white shadow-sm">
          <Milk className="h-5 w-5" />
        </div>
      )}
      <div className="leading-tight">
        <div className="text-sm font-semibold tracking-tight" data-testid="brand-name">{brandName || "LactoPilot"}</div>
        <div className="text-[11px] text-muted-foreground">{t("footer.brandSub")}</div>
      </div>
    </div>
  );
};

const navLinkClass = ({ isActive }) =>
  `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive
      ? "bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))] border-l-2 border-[hsl(var(--brand))] pl-[10px]"
      : "text-slate-600 hover:bg-secondary hover:text-foreground"
  }`;

const NavItem = ({ to, labelKey, icon: Icon, end, testid, onNavigate }) => {
  const { t } = useT();
  return (
    <NavLink to={appPath(to)} end={end} data-testid={testid} onClick={onNavigate} className={navLinkClass}>
      <Icon className="h-[18px] w-[18px]" />
      {t(labelKey)}
    </NavLink>
  );
};

const NavModule = ({ module, onNavigate, expanded, onToggle }) => {
  const { t } = useT();
  const { canAccess, user } = useAuth();
  const visible = module.items.filter((n) =>
    n.roleOnly ? n.roleOnly.includes(user?.role) : canAccess(n.sectionKey || keyOf(n.to))
  );
  if (visible.length === 0) return null;
  const Icon = module.icon;
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        data-testid={`nav-module-${module.id}`}
        className="nav-group-toggle flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
      >
        <Icon className="h-4 w-4" />
        <span className="flex-1 text-left">{t(module.labelKey)}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? "" : "-rotate-90"}`} />
      </button>
      {expanded && (
        <div className="mt-1 flex flex-col gap-1">
          {visible.map((item) => (
            <NavItem key={item.to} {...item} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
};

const NavItems = ({ onNavigate, openModules, toggleModule }) => {
  const { canAccess } = useAuth();
  // Section-filtered top-level nav links — recomputes when access (role/permissions) changes.
  const topNav = useMemo(
    () => TOP_NAV.filter((item) => canAccess(keyOf(item.to))),
    [canAccess],
  );
  return (
    <nav className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto thin-scroll px-2 pb-4">
      {topNav.map((item) => (
        <NavItem key={item.to} {...item} onNavigate={onNavigate} />
      ))}
      {MODULES.map((module) => (
        <NavModule
          key={module.id}
          module={module}
          onNavigate={onNavigate}
          expanded={openModules.has(module.id)}
          onToggle={() => toggleModule(module.id)}
        />
      ))}
    </nav>
  );
};

const pageTitle = (pathname, t) => {
  const found = ALL_NAV.find((n) => (n.end ? pathname === appPath(n.to) : pathname.startsWith(appPath(n.to))));
  return found ? t(found.labelKey) : "LactoPilot";
};

const initials = (name = "") =>
  name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() || "U";

const UserMenu = () => {
  const { user, signOut } = useAuth();
  const { t } = useT();
  const [pwOpen, setPwOpen] = useState(false);
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);
  if (!user) return null;

  const submitPw = async () => {
    if (next.length < 6) { toast.error(t("changePw.tooShort")); return; }
    if (next !== next2) { toast.error(t("changePw.mismatch")); return; }
    setSaving(true);
    try {
      await changePassword(cur, next);
      toast.success(t("changePw.success"));
      setPwOpen(false); setCur(""); setNext(""); setNext2("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("changePw.failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center gap-2 pl-1.5 pr-2" data-testid="user-menu-button">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-xs font-semibold text-white">
            {initials(user.name)}
          </span>
          <span className="hidden text-left sm:block">
            <span className="block text-sm font-medium leading-none">{user.name}</span>
            <span className="block text-[11px] capitalize text-muted-foreground">{user.role}</span>
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>
          <div className="text-sm font-medium">{user.name}</div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setPwOpen(true)} data-testid="change-password-menu">
          <KeyRound className="mr-2 h-4 w-4" /> {t("changePw.menu")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={signOut} data-testid="logout-button" className="text-[hsl(var(--danger))]">
          <LogOut className="mr-2 h-4 w-4" /> {t("userMenu.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <Dialog open={pwOpen} onOpenChange={setPwOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("changePw.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("changePw.current")}</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="change-pw-current" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("changePw.new")}</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="change-pw-new" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("changePw.confirm")}</Label>
            <Input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitPw()} data-testid="change-pw-confirm" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPwOpen(false)}>{t("common.cancel")}</Button>
          <Button onClick={submitPw} disabled={saving} data-testid="change-pw-submit">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export const Layout = ({ children, actions }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useT();
  const role = user?.role;
  const [open, setOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const [branding, setBranding] = useState(null);

  // Load per-tenant branding (name/logo/accent) and apply the accent color globally.
  useEffect(() => {
    if (!user || role === "client" || role === "superadmin") return;
    let alive = true;
    getCompanySettings().then((c) => {
      if (!alive) return;
      setBranding(c);
      const hsl = hexToHsl(c?.accent_color);
      if (hsl) document.documentElement.style.setProperty("--brand", hsl);
    }).catch(() => { /* branding is optional */ });
    return () => { alive = false; };
  }, [user, role]);

  const activeModuleId = moduleForPath(location.pathname);
  const [openModules, setOpenModules] = useState(() => {
    let saved = [];
    try { saved = JSON.parse(localStorage.getItem(NAV_STORAGE_KEY) || "[]"); } catch { saved = []; }
    const set = new Set(Array.isArray(saved) ? saved : []);
    if (activeModuleId) set.add(activeModuleId);
    return set;
  });

  // Auto-expand the module that owns the current route when navigating.
  useEffect(() => {
    if (!activeModuleId) return;
    setOpenModules((prev) => {
      if (prev.has(activeModuleId)) return prev;
      const next = new Set(prev);
      next.add(activeModuleId);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModuleId]);

  const toggleModule = (id) => {
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify([...next])); } catch { /* preference only */ }
      return next;
    });
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-[220px] shrink-0 flex-col border-r border-border bg-card lg:flex xl:w-[260px]">
        <div className="flex h-16 items-center border-b border-border">
          <Brand brandName={branding?.brand_name} logoUrl={branding?.logo_url} />
        </div>
        <NavItems openModules={openModules} toggleModule={toggleModule} />
        <div className="mt-auto p-4 text-[11px] text-muted-foreground">
          {t("footer.units")}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar: single row on desktop; on phones/tablets the page actions drop to a second,
            horizontally scrollable row so the bell, language and user menu never get pushed off-screen. */}
        <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur">
          <div className="flex h-14 items-center gap-2 px-3 sm:px-6 lg:h-16 lg:gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0 lg:hidden" data-testid="mobile-menu-button" aria-label="Menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="flex w-[min(300px,85vw)] flex-col p-0">
                <div className="flex h-14 shrink-0 items-center border-b border-border lg:h-16">
                  <Brand brandName={branding?.brand_name} logoUrl={branding?.logo_url} />
                </div>
                <NavItems openModules={openModules} toggleModule={toggleModule} onNavigate={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <h1 className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight lg:flex-none lg:text-lg" data-testid="page-title">
              {pageTitle(location.pathname, t)}
            </h1>
            <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-2">
              {isDesktop && actions}
              {isDesktop && actions ? <div className="ml-1 hidden h-8 w-px bg-border sm:block" /> : null}
              <LanguageSwitcher />
              <NotificationCenter />
              <UserMenu />
            </div>
          </div>
          {!isDesktop && actions ? (
            <div className="no-scrollbar flex items-center gap-2 overflow-x-auto px-3 pb-2 sm:px-6 [&>*]:shrink-0" data-testid="mobile-page-actions">
              {actions}
            </div>
          ) : null}
        </header>

        <main className="mx-auto w-full min-w-0 max-w-[1400px] flex-1 px-3 py-4 pb-24 sm:px-6 sm:py-6 lg:px-8 lg:pb-6">
          <InstallHint />
          {children}
        </main>
      </div>
      <QuickActions />
    </div>
  );
};
